import * as THREE from 'three';
import { CAMERA } from '../core/SimConfig.js';
import { DEG2RAD } from '../utils.js';

const _offset = new THREE.Vector3();
const _matrix = new THREE.Matrix4();
const PITCH_MIN = -85 * DEG2RAD;
const PITCH_MAX =  85 * DEG2RAD;
const INITIAL_PITCH = -10 * DEG2RAD;

export default class EnemyCamera {
  constructor({ terrainCollider = null } = {}) {
    this.entity = null;
    this.terrainCollider = terrainCollider;
    this.position = new THREE.Vector3();
    this.quaternion = new THREE.Quaternion();

    // Orbit angles — target is driven by mouse, current eases toward it
    this.yaw = 0;
    this.pitch = INITIAL_PITCH;
    this.targetYaw = 0;
    this.targetPitch = INITIAL_PITCH;

    // Orbit radius — driven by the scroll wheel, eased the same way
    this.distance = CAMERA.enemy.distance;
    this.targetDistance = CAMERA.enemy.distance;
  }

  setEntity(entity, snap = false) {
    this.entity = entity;
    if (snap) {
      // Seed yaw to the entity's heading so the camera starts behind it
      const h = entity?.flight?.heading;
      if (typeof h === 'number') this.targetYaw = h;
      this.targetPitch = INITIAL_PITCH;
      this.yaw = this.targetYaw;
      this.pitch = this.targetPitch;
      this.targetDistance = CAMERA.enemy.distance;
      this.distance = this.targetDistance;
    }
  }

  applyMouseDelta(dx, dy, sensitivity) {
    this.targetYaw -= dx * sensitivity;
    this.targetPitch -= dy * sensitivity;
    if (this.targetPitch < PITCH_MIN) this.targetPitch = PITCH_MIN;
    if (this.targetPitch > PITCH_MAX) this.targetPitch = PITCH_MAX;
  }

  /** Scroll-wheel zoom — multiplicative so each notch feels the same at any radius. */
  applyZoom(deltaY) {
    const cfg = CAMERA.enemy;
    const f = deltaY < 0 ? 1 / cfg.zoomFactor : cfg.zoomFactor;
    this.targetDistance = Math.max(
      cfg.minDistance,
      Math.min(cfg.maxDistance, this.targetDistance * f),
    );
  }

  _entityPosition() {
    return this.entity?.group?.position || this.entity?.position || null;
  }

  update(dt) {
    if (!this.entity) return;
    const target = this._entityPosition();
    if (!target) return;

    const cfg = CAMERA.enemy;

    // Ease orbit angles + radius toward their targets — smooths raw pointer-lock
    // deltas and the discrete wheel notches.
    const alpha = 1 - Math.exp(-cfg.lookSmoothing * dt);
    this.yaw += (this.targetYaw - this.yaw) * alpha;
    this.pitch += (this.targetPitch - this.pitch) * alpha;
    const zoomAlpha = 1 - Math.exp(-cfg.zoomSmoothing * dt);
    this.distance += (this.targetDistance - this.distance) * zoomAlpha;

    // Rigid spherical orbit, focal point pinned to the entity (no lag)
    const cosP = Math.cos(this.pitch);
    _offset.set(
      Math.sin(this.yaw) * cosP,
      -Math.sin(this.pitch),
      Math.cos(this.yaw) * cosP,
    );
    this.position.copy(target).addScaledVector(_offset, this.distance);

    // Keep the orbit out of the ground: a low-slung enemy (or a wide zoom on a
    // rising slope) would otherwise swing the camera under the terrain. Lifting
    // the position before the lookAt keeps the entity framed — the orbit just
    // flattens out where it would have sunk.
    if (this.terrainCollider) {
      const groundY = this.terrainCollider.getHeightAt(this.position.x, this.position.z) ?? 0;
      const floor = groundY + cfg.groundClearance;
      if (this.position.y < floor) this.position.y = floor;
    }

    _matrix.lookAt(this.position, target, THREE.Object3D.DEFAULT_UP);
    this.quaternion.setFromRotationMatrix(_matrix);
  }
}
